import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Pencil, Trash2, X } from "lucide-react";

const FIELD_TYPES = [
  ["text", "Short text"], ["textarea", "Long text"], ["select", "Dropdown"],
  ["multiselect", "Multi-select"], ["number", "Number"], ["date", "Date"], ["boolean", "Yes / No"],
];
const newUid = () => Math.random().toString(36).slice(2, 9);
const EMPTY_FIELD = { label: "", type: "text", options: [], units: "", required: false, help: "" };
const EMPTY_TPL = { name: "", specimen_type: "", description: "", fields: [{ ...EMPTY_FIELD, uid: "f1" }] };

export default function Templates() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [specimenTypes, setSpecimenTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TPL);
  const canEdit = ["pathologist", "admin", "lab_director"].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [t, lab] = await Promise.all([api.get("/templates"), api.get("/lab")]);
      setRows(t.data);
      setSpecimenTypes(lab.data.specimen_types || []);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm({ ...EMPTY_TPL, fields: [{ ...EMPTY_FIELD, uid: newUid() }] }); setOpen(true); };
  const startEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, specimen_type: t.specimen_type, description: t.description, fields: t.fields.map((f) => ({ ...f, uid: newUid() })) });
    setOpen(true);
  };

  const setField = (i, patch) => setForm((f) => ({ ...f, fields: f.fields.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      fields: form.fields.filter((f) => f.label.trim()).map(({ uid, ...f }) => ({
        ...f,
        options: typeof f.options === "string" ? f.options.split(",").map((o) => o.trim()).filter(Boolean) : f.options,
      })),
    };
    if (!payload.name.trim() || payload.fields.length === 0) {
      toast.error("Give the template a name and at least one field");
      return;
    }
    try {
      if (editing) await api.put(`/templates/${editing.id}`, payload);
      else await api.post("/templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const retire = async (t) => {
    if (!window.confirm(`Retire "${t.name}"? Cases already using it keep their data.`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Template retired");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="templates-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Synoptic Templates</h1>
          <p className="mt-1 text-sm text-slate-600">
            {rows.length} template(s). Pathologists fill these on a case so every report reads consistently.
          </p>
        </div>
        {canEdit && (
          <Button onClick={startNew} data-testid="new-template-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={15} className="mr-2" /> New template
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((t) => (
          <article key={t.id} className="flex flex-col border border-slate-200 p-4" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start gap-2">
              <ClipboardCheck size={16} className="mt-0.5 shrink-0 text-[#134e4a]" />
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-slate-900 md:text-lg">{t.name}</h2>
                <p className="text-xs uppercase tracking-wide text-slate-500">{t.specimen_type || "Any specimen type"}</p>
              </div>
            </div>
            {t.description && <p className="mt-2 text-sm text-slate-600">{t.description}</p>}
            <ul className="mt-3 space-y-0.5 text-xs text-slate-600">
              {t.fields.slice(0, 6).map((f) => (
                <li key={f.key} className="truncate">• {f.label}{f.required ? " *" : ""}</li>
              ))}
              {t.fields.length > 6 && <li className="text-slate-400">+ {t.fields.length - 6} more</li>}
            </ul>
            {canEdit && (
              <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
                <Button size="sm" variant="outline" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`} className="h-10">
                  <Pencil size={13} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => retire(t)} data-testid={`template-retire-${t.id}`} className="h-10 text-red-800">
                  <Trash2 size={13} className="mr-1" /> Retire
                </Button>
              </div>
            )}
          </article>
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="templates-empty">No templates yet.</p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit template" : "New synoptic template"}</DialogTitle>
            <DialogDescription>
              Name the template, tie it to a specimen type, then add the fields pathologists should fill on each case.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="template-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Template name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="template-name-input" className="mt-1 h-11 border-slate-300" placeholder="e.g. Thyroid — Papillary carcinoma" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Specimen type</Label>
                <select value={form.specimen_type} onChange={(e) => setForm((f) => ({ ...f, specimen_type: e.target.value }))}
                  data-testid="template-specimen-select" className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Any specimen type</option>
                  {specimenTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">Matching cases pre-select this template.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Description</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="template-description-input" className="mt-1 h-11 border-slate-300" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-800 md:text-lg">Fields</h3>
                <Button type="button" size="sm" variant="outline" data-testid="add-field-button" className="h-10"
                  onClick={() => setForm((f) => ({ ...f, fields: [...f.fields, { ...EMPTY_FIELD, uid: newUid() }] }))}>
                  <Plus size={13} className="mr-1" /> Add field
                </Button>
              </div>
              {form.fields.map((f, i) => (
                <div key={f.uid || `f-${i}`} className="grid gap-2 border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[1.6fr_1fr_1fr_auto]" data-testid={`template-field-row-${i}`}>
                  <Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })}
                    placeholder="Field label" data-testid={`field-label-${i}`} className="h-11 border-slate-300 bg-white" />
                  <select value={f.type} onChange={(e) => setField(i, { type: e.target.value })}
                    data-testid={`field-type-${i}`} className="h-11 rounded-md border border-slate-300 bg-white px-2 text-sm">
                    {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {["select", "multiselect"].includes(f.type) ? (
                    <Input value={Array.isArray(f.options) ? f.options.join(", ") : f.options}
                      onChange={(e) => setField(i, { options: e.target.value })}
                      placeholder="Option 1, Option 2" data-testid={`field-options-${i}`} className="h-11 border-slate-300 bg-white" />
                  ) : (
                    <Input value={f.units} onChange={(e) => setField(i, { units: e.target.value })}
                      placeholder="Units (mm, %)" data-testid={`field-units-${i}`} className="h-11 border-slate-300 bg-white" />
                  )}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs font-semibold text-slate-600">
                      <input type="checkbox" checked={Boolean(f.required)} onChange={(e) => setField(i, { required: e.target.checked })}
                        data-testid={`field-required-${i}`} className="h-4 w-4 accent-[#134e4a]" /> Req
                    </label>
                    <button type="button" onClick={() => setForm((x) => ({ ...x, fields: x.fields.filter((_, j) => j !== i) }))}
                      data-testid={`field-remove-${i}`} className="inline-flex h-10 w-10 items-center justify-center text-slate-500 hover:text-red-800" aria-label="Remove field">
                      <X size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="template-cancel-button" className="h-11">Cancel</Button>
              <Button type="submit" data-testid="template-save-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                {editing ? "Save changes" : "Create template"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
